import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Avatar,
  Button,
  Box,
} from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SocialMediaIcons from "./SocialMediaIcons";
import useWindowDimensions from "../windowDimensions";
import { sportmap } from "../utilities/sportmap";
import { nameToSlug } from "./PhotoUpload";
import athletesMostRecent from "../data/athletesMostRecentBackup.json";

const NO_PHOTO_URL = "https://plantbuilt.s3.us-west-2.amazonaws.com/profile-photos/plantbuilt-no-photo-square.png";

const ProfileImage = ({ athlete, sx }) => {
  const squareSrc = athlete.img
    ? `${athlete.img.substring(0, athlete.img.length - 4)}-square.png`
    : null;
  const [src, setSrc] = useState(squareSrc || athlete.img || NO_PHOTO_URL);

  useEffect(() => {
    const sq = athlete.img
      ? `${athlete.img.substring(0, athlete.img.length - 4)}-square.png`
      : null;
    setSrc(sq || athlete.img || NO_PHOTO_URL);
  }, [athlete.img]);

  const handleError = () => {
    const sq = athlete.img
      ? `${athlete.img.substring(0, athlete.img.length - 4)}-square.png`
      : null;
    if (src === sq && athlete.img) setSrc(athlete.img);
    else if (src !== NO_PHOTO_URL) setSrc(NO_PHOTO_URL);
  };

  return <CardMedia component="img" sx={sx} image={src} alt={athlete.name} onError={handleError} />;
};

const AthleteProfile = () => {
  const [athletesData, setAthletesData] = useState(athletesMostRecent);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { athleteSlug } = useParams();
  const history = useHistory();
  const { width } = useWindowDimensions();

  useEffect(() => {
    (async () => {
      try {
        const data = await (
          await fetch(
            "https://plantbuilt.s3.us-west-2.amazonaws.com/athletes.json"
          )
        ).json();
        if (data && data.length) {
          setAthletesData(data.filter((a) => a.est !== 2024));
        }
      } catch (e) {
        // keep backup data
      } finally {
        setDataLoaded(true);
      }
    })();
  }, []);

  const athlete = athletesData.find((a) => nameToSlug(a.name) === athleteSlug);

  if (!athlete || athlete.published === false) {
    if (!dataLoaded) return null;
    return (
      <Box sx={{ textAlign: "center", marginTop: "40px" }}>
        <Typography variant="h5">Athlete not found.</Typography>
        <Button
          onClick={() => history.push("/profiles")}
          sx={{ marginTop: "16px" }}
        >
          ← All Athletes
        </Button>
      </Box>
    );
  }

  const sport = [sportmap[athlete.sport], sportmap[athlete.secondSport]]
    .filter(Boolean)
    .join(" & ");

  const handleFindDate = (dateString) => {
    if (dateString.length === 4) dateString = "01/01/" + dateString;
    var today = new Date();
    var birthDate = new Date(dateString);
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const renderAthleteSocial = () => {
    if (athlete.social) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: width > 900 ? "flex-start" : "center",
          }}
        >
          {athlete.social.map((element, elemIdx) => (
            <SocialMediaIcons key={elemIdx} socialLink={element} />
          ))}
        </div>
      );
    }
    return "";
  };

  const renderAthleteBio = () => {
    if (athlete.bio) {
      return athlete.bio.map((question) => {
        if (question.key === "Birth Year") {
          return (
            <Typography
              key={question.key}
              style={{
                fontFamily: "Roboto",
                fontSize: "1.5rem",
                lineHeight: "3rem",
              }}
              paragraph
              variant="body1"
            >
              <b style={{ color: "#769525", fontFamily: "Impact" }}>Age: </b>
              {question.value && parseInt(question.value)
                ? handleFindDate(question.value)
                : "Vegans are Ageless"}
            </Typography>
          );
        } else if (question.key === "Vegan Since") {
          return (
            <Typography
              key={question.key}
              style={{
                fontFamily: "Roboto",
                fontSize: "1.5rem",
                lineHeight: "3rem",
              }}
              paragraph
              variant="body1"
            >
              <b style={{ color: "#769525", fontFamily: "Impact" }}>
                Years as a Vegan:{" "}
              </b>
              {question.value && parseInt(question.value)
                ? handleFindDate(question.value)
                : "Unsure"}
            </Typography>
          );
        } else if (question.key === "Competition Highlights") {
          return (
            <div key={question.key}>
              <Typography
                variant="body1"
                style={{
                  color: "#769525",
                  fontFamily: "Impact",
                  fontSize: "1.5rem",
                }}
              >
                <b>{question.key}</b>:
              </Typography>
              {question.value.reverse().map((event, eventIdx) => (
                <div style={{ marginBottom: "32px" }} key={eventIdx}>
                  <p
                    style={{
                      fontFamily: "Roboto",
                      fontSize: "1.5rem",
                      marginTop: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    {event.title}
                  </p>
                  <ul style={{ margin: "8px 0px" }}>
                    {(event.placings || []).map((placing, placingIdx) => (
                      <li
                        style={{
                          fontFamily: "Roboto",
                          fontSize: "1.25rem",
                        }}
                        key={placingIdx}
                      >
                        {placing}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <Typography
              key={question.key}
              style={{
                fontFamily: "Roboto",
                fontSize: "1.5rem",
                lineHeight: "3rem",
              }}
              paragraph
              variant="body1"
            >
              <b style={{ color: "#769525", fontFamily: "Impact" }}>
                {question.key}:{" "}
              </b>
              {question.value}
            </Typography>
          );
        }
      });
    }
    return "Bio coming soon!";
  };

  const bioSnippet =
    (Array.isArray(athlete.bio) ? athlete.bio : [])
      .find(
        (q) =>
          q.key !== "Birth Year" &&
          q.key !== "Competition Highlights" &&
          q.value &&
          typeof q.value === "string"
      )
      ?.value?.slice(0, 160) || "";

  return (
    <>
      <Helmet>
        <title>{`PlantBuilt: ${athlete.name}`}</title>
        <meta property="og:title" content={`PlantBuilt: ${athlete.name}`} />
        <meta property="og:description" content={bioSnippet} />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`https://plantbuilt.com${
            athlete.meta || "/plantbuilt-default-meta.png"
          }`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`PlantBuilt: ${athlete.name}`} />
        <meta name="twitter:description" content={bioSnippet} />
        <meta
          name="twitter:image"
          content={`https://plantbuilt.com${
            athlete.meta || "/plantbuilt-default-meta.png"
          }`}
        />
      </Helmet>
      <Box sx={{ maxWidth: "1200px", margin: "20px auto", padding: "0 16px" }}>
        <Button
          onClick={() => history.push("/profiles")}
          sx={{ marginBottom: "16px" }}
        >
          ← All Athletes
        </Button>
        <Card>
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: "#709425" }} aria-label="athlete">
                <FitnessCenterIcon />
              </Avatar>
            }
            title={athlete.name}
            subheader={`${sport}${
              athlete.name === "Giacomo Marchese" ||
              athlete.name === "Dani Taylor"
                ? " - PlantBuilt Co-Founder"
                : ""
            }`}
          />
          <div
            style={{
              display: "flex",
              flexDirection: width > 900 ? "row" : "column",
            }}
          >
            <div>
              <ProfileImage
                athlete={athlete}
                sx={{
                  width: width > 900 ? "400px" : "450px",
                  margin: width > 900 ? "0px 0px 0px 15px" : "auto",
                }}
              />
              <CardContent>{renderAthleteSocial()}</CardContent>
            </div>
            <div>
              <CardContent sx={{ paddingTop: "0px" }}>
                {renderAthleteBio()}
              </CardContent>
            </div>
          </div>
        </Card>
      </Box>
    </>
  );
};

export default AthleteProfile;
